'use client';

import { useState } from 'react';

import { useIsomorphicLayoutEffect } from '../useIsomorphicLayoutEffect/useIsomorphicLayoutEffect';

export type ImageLoadingStatus = 'idle' | 'loading' | 'loaded' | 'error';

/**
 * Tracks the load status of an image source through a hidden `Image()` preload,
 * so consumers can render different content per state without ever mounting a
 * broken `<img>`. The cached-image fast path (`image.complete`) resolves
 * synchronously on mount, preventing a fallback flash.
 *
 * Adapted from Radix UI Avatar:
 * https://github.com/radix-ui/primitives/blob/22473d16404bfd446305db5b6c9308aece99fdec/packages/react/avatar/src/avatar.tsx#L133
 */
export function useImageLoadingStatus(src?: string): ImageLoadingStatus {
  const [status, setStatus] = useState<ImageLoadingStatus>(() =>
    src ? 'loading' : 'idle'
  );

  useIsomorphicLayoutEffect(() => {
    if (!src) {
      setStatus('idle');
      return;
    }

    let isMounted = true;
    const image = new window.Image();

    const updateStatus = (next: ImageLoadingStatus) => () => {
      if (isMounted) {
        setStatus(next);
      }
    };

    setStatus('loading');
    image.onload = updateStatus('loaded');
    image.onerror = updateStatus('error');
    image.src = src;

    if (image.complete && image.naturalWidth > 0) {
      setStatus('loaded');
    }

    return () => {
      isMounted = false;
      image.onload = null;
      image.onerror = null;
    };
  }, [src]);

  return status;
}
